import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useBodyScrollLock } from '@src/components/common/useBodyScrollLock';
import CloseIcon from '@src/svgs/CloseIcon';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

type Props = {
  isOpen: boolean;
  closeModal?: () => void;
  backgroundColor?: string;
  children: React.ReactNode;
  contentStyle?: React.CSSProperties;
  closeButtonStyle?: React.CSSProperties;
  crossColor?: string;
  blurBackground?: boolean;
  disableEscapeClose?: boolean;
  /** Override the backdrop z-index (e.g. to paint above a high-z drawer). */
  backdropZIndex?: number;
};

export default function Modal(props: Props) {
  const {
    isOpen,
    closeModal,
    children,
    backgroundColor = 'var(--modal-backdrop)',
    contentStyle,
    closeButtonStyle,
    crossColor = 'var(--text-secondary)',
    blurBackground = false,
    disableEscapeClose = false,
    backdropZIndex,
  } = props;

  // While a dialog is up, the wheel belongs to it. Without this the page behind
  // the backdrop scrolls whenever the pointer is over dialog chrome rather than
  // over a scroller of its own — and whenever an inner list has been scrolled to
  // its end, since the chain runs list -> backdrop -> document. `overscroll-
  // behavior: contain` on the backdrop only holds while the backdrop itself
  // scrolls (a dialog taller than the viewport), so the lock is what covers the
  // ordinary case. Shared with the older `src/components/Modal.tsx`, which the
  // two of them nest with — see the hook for why one counter serves both.
  useBodyScrollLock(isOpen);

  // Support Esc key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !closeModal || disableEscapeClose) return;
      // Escape stops here. Hosts underneath keep their own `window`-level
      // Escape listeners (the clip preview closes itself on one), and this
      // listener is on `document` — one bubble step earlier — so without this
      // the same keypress closes this dialog *and* whatever it opened from.
      //
      // It must be propagation, not a "is a dialog open?" DOM probe: browsers
      // run a microtask checkpoint between listener callbacks, so React has
      // already unmounted this modal — backdrop and all — by the time a
      // `window` listener looks. (jsdom does not, which is why that mistake
      // tests green.)
      e.stopPropagation();
      closeModal();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // `disableEscapeClose` is read inside the handler, so it has to re-subscribe
    // when it flips — a caller that raises the guard mid-session (a nested
    // confirm, an in-flight request) would otherwise keep the listener that
    // closed over the old value.
  }, [isOpen, closeModal, disableEscapeClose]);

  return createPortal(
    <>
      {isOpen ? (
        <>
          <Wrapper
            backgroundColor={backgroundColor}
            $blur={blurBackground}
            $backdropZIndex={backdropZIndex}
          >
            <div
              style={{ position: 'relative', ...contentStyle }}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {closeModal && (
                <CloseButton
                  onClick={() => closeModal()}
                  aria-label="Close modal"
                  style={closeButtonStyle}
                >
                  <CloseIcon strokeColor={crossColor} />
                </CloseButton>
              )}

              {children}
            </div>
          </Wrapper>
        </>
      ) : null}
    </>,
    document.body,
  );
}

const Wrapper = styled.div
  .withConfig({ displayName: 'ModalWrapper' })
  // `data-modal-backdrop` is read by code that has to stand down while a dialog
  // is up — the clip preview's Space-to-play, which would otherwise toggle the
  // video behind this backdrop. A marker rather than the testid, so behaviour
  // never hangs off a test hook.
  .attrs({ 'data-testid': 'modal-wrapper', 'data-modal-backdrop': 'true' })<{
  backgroundColor: string;
  $blur: boolean;
  $backdropZIndex?: number;
}>`
  background-color: ${props => props.backgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  overflow-y: auto;
  /* A tall dialog scrolls the backdrop; the wheel then stops there rather than
     carrying on into the page underneath. */
  overscroll-behavior: contain;
  position: fixed;
  inset: 0;
  /*
   * Read the top-layer backdrop var, falling back to this modal's historical
   * resting z when the var is unset (PLAT-10258 F1). At rest the var is unset
   * everywhere, so this stays 1000010 -- unchanged app-wide. Only a workspace
   * mounted above the clip-preview stack (the Translation Review clip-preview
   * entry) sets the top-layer-modal-backdrop-z var (via liftOverlays), lifting
   * any shared Modal opened inside it -- e.g. the DirectedRetranslateModal --
   * above that workspace overlay (z 1000030) instead of painting behind it.
   */
  z-index: ${props => props.$backdropZIndex ?? `var(${TOP_LAYER_VARS.modalBackdrop}, 1000010)`};
  backdrop-filter: var(--modal-blur);
  outline: none;
  ${props => props.$blur && 'backdrop-filter: blur(8px);'}
`;

const CloseButton = styled.button
  .withConfig({ displayName: 'ModalCloseButton' })
  .attrs({ 'data-testid': 'modal-close-button' })`
  position: absolute;
  z-index: 10;
  padding: var(--spacing-sm);
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  color: var(--text-muted);
  background-color: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;
